/**
 * @file Renders the distribution schedule's next date, or a paused tag when it is on hold.
 */
import { Tag } from "antd";
import { PauseCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { Bold, Value } from "@components/Text";
import type { ScheduleStatus } from "@api/types";
import { HOLD as SCHEDULE_STATUS_HOLD } from "@api/services/transactions/shapes/ScheduleStatusEnum";

const LABEL_NEXT_DATE = "Next Date";
const LABEL_PAUSED = "Schedule is paused";
const DATE_FORMAT = "MM/DD/YYYY";

type Props = {
  status?: ScheduleStatus;
  nextDate?: string;
  totalAmount?: number;
}

/**
 * Renders the schedule's paused tag, or its next distribution date and total amount.
 */
const DistributionsScheduleSummary = ({
  status = undefined,
  nextDate = undefined, 
  totalAmount = undefined, 
}: Props) => {
  const isHold = status === SCHEDULE_STATUS_HOLD;

  if (!nextDate) {
    return null;
  }

  const value = dayjs(nextDate).format(DATE_FORMAT);

  if (isHold) {
    return (
      <Tag color="orange" icon={<PauseCircleOutlined />} bordered={false}>
        {LABEL_PAUSED}
      </Tag>
    );
  }

  return (
    <span>
      {`${LABEL_NEXT_DATE}:`} <Bold>{value}</Bold>

      {
        totalAmount && (
          <span>{' for '}<Bold><Value value={totalAmount} /></Bold></span>
        )
      }
    </span>
  );
};

export default DistributionsScheduleSummary;
